import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useAuth } from '@/lib/auth/useAuth';
import { useLandAfterAuth } from '@/lib/auth/useLandAfterAuth';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackRoute,
});

interface Failure {
  title: string;
  detail: string;
  retryTo: string;
  retryLabel: string;
}

/**
 * Supabase reports a rejected link (expired/invalid OTP, denied consent) via
 * `error` / `error_code` / `error_description` on the redirect — in the query
 * string AND mirrored in the hash fragment. Read both. Returns null when the
 * arrival is a normal success (no error present).
 */
function readAuthError(): { code: string; description: string } | null {
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const error = query.get('error') ?? hash.get('error');
  if (!error) return null;
  const code = query.get('error_code') ?? hash.get('error_code') ?? error;
  const description = query.get('error_description') ?? hash.get('error_description') ?? '';
  return { code, description };
}

function AuthCallbackRoute() {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const land = useLandAfterAuth();
  const [failure, setFailure] = useState<Failure | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isRecovery = params.get('intent') === 'recovery';

    // An expired or already-used link comes back as an error param, not a code.
    // Surface it plainly instead of looping the session poll forever.
    const authError = readAuthError();
    if (authError) {
      const expired = authError.code === 'otp_expired' || authError.code === 'access_denied';
      setFailure({
        title: expired ? 'This link has expired' : 'This link didn’t work',
        detail:
          authError.description ||
          'The link is invalid or has already been used. Request a fresh one and open it right away.',
        retryTo: isRecovery ? '/auth/reset-password' : '/auth/sign-in',
        retryLabel: isRecovery ? 'Request a new reset link' : 'Back to sign in',
      });
      return;
    }

    // Supabase's PKCE flow (default in @supabase/ssr) delivers the auth code
    // as a ?code=... query param — it does NOT auto-exchange the way the old
    // implicit-flow hash tokens did. Call exchangeCodeForSession explicitly
    // when a code is present, then fall through to the session poll for the
    // legacy implicit/magic-link/OAuth-hash path.
    const handle = async () => {
      try {
        const code = params.get('code');
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) throw exchangeError;
        }

        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        if (data.session) {
          await refreshProfile();
          // Precedence chain (never a hardcoded '/'): a next carried through the
          // verification/OAuth redirect is re-sanitized and honored, subject to
          // suspended → recovery → onboarding. A password-reset link arrives with
          // ?intent=recovery and routes to /auth/update-password before landing.
          await land(params.get('next'), { recovery: isRecovery });
        } else {
          // Give the listener one more tick (covers the implicit-flow case
          // where tokens land in the hash and the SDK exchanges them async).
          window.setTimeout(handle, 200);
        }
      } catch (err) {
        setFailure({
          title: 'Sign-in didn’t complete',
          detail: err instanceof Error ? err.message : 'Authentication failed',
          retryTo: '/auth/sign-in',
          retryLabel: 'Try again',
        });
      }
    };
    void handle();
  }, [navigate, refreshProfile, land]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-parchment text-ink">
      {failure ? (
        <div className="max-w-sm text-center">
          <h1 className="font-serif text-2xl">{failure.title}</h1>
          <p className="mt-3 font-sans text-sm text-ink/70">{failure.detail}</p>
          <button
            type="button"
            onClick={() => void navigate({ to: failure.retryTo })}
            className="mt-6 rounded-md border border-ink/20 px-4 py-2 text-sm font-medium hover:border-oxblood hover:text-oxblood"
          >
            {failure.retryLabel}
          </button>
        </div>
      ) : (
        <p className="font-sans text-sm uppercase tracking-[0.18em] text-ink/40">Signing you in…</p>
      )}
    </div>
  );
}
